import { useCallback, useMemo, useRef, useState } from 'react';

import {
  GeneratedCreateDrawEntryIntentMutation,
  GeneratedCustomAttributeInput,
  GeneratedMoneyInput,
  GeneratedOrderItem,
  useCreateDrawEntryIntentMutation,
} from '../../../types/generated';
import { captureError } from '../../../utils/capture';
import { bidFloorFromError } from '../utils/auctionBid';

type DrawEntryIntentCreateResponse = NonNullable<
  GeneratedCreateDrawEntryIntentMutation['createDrawEntryIntent']
>;

export type DrawEntryIntent = DrawEntryIntentCreateResponse['intent'];

export type DrawEntryIntentPricing =
  DrawEntryIntentCreateResponse['pricing']['pricing'];

export type DrawEntryIntentInput = {
  bid?: GeneratedMoneyInput | null;
  drawId: string;
  currencyCode: string;
  shippingAddressId?: string | null;
  paymentMethodId?: string | null;
  preOrder?: boolean;
  optionalItems: GeneratedOrderItem[];
  customAttributes?: GeneratedCustomAttributeInput[];
};

export type DrawEntryIntentController = {
  clientSecret: string | null;
  error: boolean;
  intent: DrawEntryIntent | null;
  loading: boolean;
  pricing: DrawEntryIntentPricing | null;
  reset: () => void;
  submit: (input: DrawEntryIntentInput) => Promise<DrawEntryIntent | null>;
  submittedInput: DrawEntryIntentInput | null;
};

export type UseDrawEntryIntentArgs = {
  onBidTooLow?: (floor: GeneratedMoneyInput) => void;
};

export const useDrawEntryIntent = ({
  onBidTooLow,
}: UseDrawEntryIntentArgs = {}): DrawEntryIntentController => {
  const [mutateCreate] = useCreateDrawEntryIntentMutation();

  const [intent, setIntent] = useState<DrawEntryIntent | null>(null);
  const [pricing, setPricing] = useState<DrawEntryIntentPricing | null>(null);
  const [submittedInput, setSubmittedInput] =
    useState<DrawEntryIntentInput | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestRef = useRef(0);

  const reset = useCallback(() => {
    requestRef.current += 1;
    setIntent(null);
    setPricing(null);
    setSubmittedInput(null);
    setClientSecret(null);
    setLoading(false);
    setError(false);
  }, []);

  const submit = useCallback(
    async (input: DrawEntryIntentInput) => {
      requestRef.current += 1;
      const requestId = requestRef.current;

      setLoading(true);
      setError(false);
      setSubmittedInput(input);

      try {
        const { data, errors } = await mutateCreate({
          variables: {
            bid: input.bid ?? null,
            drawId: input.drawId,
            currencyCode: input.currencyCode,
            shippingAddressId: input.shippingAddressId ?? null,
            paymentMethodId: input.paymentMethodId ?? null,
            optionalItems: input.optionalItems,
            preOrder: input.preOrder ?? false,
            cryptoPaymentRequested: false,
            customAttributes: input.customAttributes ?? [],
          },
        });

        const response = data?.createDrawEntryIntent;
        if (!response || errors) {
          throw new Error(
            `Unable to create draw entry intent draw_id:${input.drawId}`,
          );
        }

        if (requestId !== requestRef.current) return null;

        setIntent(response.intent);
        setPricing(response.pricing.pricing);
        setClientSecret(response.setupSecret);

        return response.intent;
      } catch (submitError) {
        const floor = bidFloorFromError(submitError);

        if (!floor && submitError instanceof Error) captureError(submitError);
        if (requestId === requestRef.current) {
          setError(true);
          setSubmittedInput(null);
          if (floor) onBidTooLow?.(floor);
        }

        return null;
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [mutateCreate, onBidTooLow],
  );

  return useMemo(
    () => ({
      clientSecret,
      error,
      intent,
      loading,
      pricing,
      reset,
      submit,
      submittedInput,
    }),
    [
      clientSecret,
      error,
      intent,
      loading,
      pricing,
      reset,
      submit,
      submittedInput,
    ],
  );
};
